import React, { useRef, useState } from "react";
import { auth } from "../../firebase";

// Admin-only: upload the Minnesota Gambling Control Board's
// "Sites Where Gambling is Conducted" file (.tsv or .txt, tab separated)
// and match every site to its bar in the app. Fills in which charity runs
// the gambling and its license number. Bars missing from the app get
// added. The state updates this list often, so re-upload it now and then.
//
// The file's columns (no header row):
// site name, organization, city, county, license #, site #, street, city, state, zip

const FUNCTION_URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/importAreaBars";
const BATCH_SIZE = 8;
const DEFAULT_COUNTIES = ["Ramsey", "Washington", "Dakota"];

function parseFile(text) {
  return text
    .split(/\r?\n/)
    .map((line) => line.split("\t").map((c) => c.trim()))
    .filter((c) => c.length >= 10 && c[0] && !/^site/i.test(c[0]))
    .map((c) => ({
      site: c[0],
      org: c[1],
      county: c[3],
      license: c[4],
      siteNumber: c[5],
      address: c[6],
      city: c[7] || c[2],
      zip: c[9],
    }));
}

const btn = {
  minHeight: "44px", padding: "0 18px", borderRadius: "10px", border: "none",
  font: "700 15px Inter, sans-serif", cursor: "pointer",
};

export default function GamingSitesImport() {
  const [rows, setRows] = useState([]);
  const [counties, setCounties] = useState(new Set(DEFAULT_COUNTIES));
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const stopRef = useRef(false);

  const allCounties = [...new Set(rows.map((r) => r.county))].sort();
  const chosen = rows.filter((r) => counties.has(r.county));

  const onFile = async (file) => {
    setError("");
    setResults([]);
    if (!file) return;
    const parsed = parseFile(await file.text());
    if (parsed.length === 0) {
      setError("That file didn't look like the state's gambling sites list. It should be the tab-separated download.");
      return;
    }
    setRows(parsed);
  };

  const toggleCounty = (c) =>
    setCounties((prev) => {
      const next = new Set(prev);
      next.has(c) ? next.delete(c) : next.add(c);
      return next;
    });

  const run = async () => {
    setRunning(true);
    setResults([]);
    setError("");
    stopRef.current = false;
    for (let i = 0; i < chosen.length; i += BATCH_SIZE) {
      if (stopRef.current) break;
      const batch = chosen.slice(i, i + BATCH_SIZE);
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(FUNCTION_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ action: "gaming", rows: batch }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
        setResults((prev) => [...prev, ...(data.results || [])]);
      } catch (e) {
        setResults((prev) => [...prev, ...batch.map((r) => ({ name: r.site, status: `error: ${e.message}` }))]);
      }
    }
    setRunning(false);
  };

  const count = (status) => results.filter((r) => r.status === status).length;

  return (
    <div style={{ background: "#17131F", borderRadius: "14px", padding: "18px", marginBottom: "16px" }}>
      <h3 style={{ margin: "0 0 6px", fontSize: "18px" }}>Charitable gaming sites (state list)</h3>
      <p style={{ margin: "0 0 14px", color: "#B8B0C8", lineHeight: 1.5 }}>
        Upload the Minnesota Gambling Control Board's "Sites Where Gambling is Conducted" file. Each site gets
        matched to its bar and shows "Operated by [charity]" in the app. Bars not in the app yet get added.
        Bars a charity account already manages are left alone.
      </p>

      <label htmlFor="gcb-file" style={{ display: "block", marginBottom: "6px", fontWeight: 600 }}>State file (.tsv)</label>
      <input id="gcb-file" type="file" accept=".tsv,.txt,text/tab-separated-values,text/plain" onChange={(e) => onFile(e.target.files?.[0])} style={{ marginBottom: "14px", color: "#fff" }} />

      {error && <p style={{ color: "#FF6B8A" }}>{error}</p>}

      {rows.length > 0 && (
        <>
          <p style={{ margin: "0 0 8px", color: "#D6D2E4" }}>
            {rows.length} sites in the file. Pick counties:
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", maxHeight: "180px", overflowY: "auto", marginBottom: "14px" }}>
            {allCounties.map((c) => {
              const on = counties.has(c);
              return (
                <button
                  key={c}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleCounty(c)}
                  style={{
                    minHeight: "36px", padding: "0 12px", borderRadius: "18px", cursor: "pointer",
                    border: on ? "none" : "1px solid #3A3350",
                    background: on ? "#FF3D81" : "transparent",
                    color: on ? "#0B0B12" : "#fff",
                    font: `${on ? 700 : 500} 14px Inter, sans-serif`,
                  }}
                >
                  {c} ({rows.filter((r) => r.county === c).length})
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={run}
              disabled={running || chosen.length === 0}
              style={{ ...btn, background: "#FF3D81", color: "#0B0B12", opacity: running ? 0.6 : 1 }}
            >
              {running ? "Matching…" : `Match ${chosen.length} gambling sites`}
            </button>
            {running && (
              <>
                <span style={{ color: "#B8B0C8" }}>{results.length} of {chosen.length}. Keep this tab open.</span>
                <button type="button" onClick={() => { stopRef.current = true; }} style={{ ...btn, background: "transparent", border: "1px solid #3A3350", color: "#fff" }}>
                  Stop
                </button>
              </>
            )}
          </div>
        </>
      )}

      {results.length > 0 && (
        <>
          <p style={{ margin: "14px 0 8px", color: "#D6D2E4" }}>
            {count("updated")} bars updated · {count("added")} new bars added · {count("charity manages it (left alone)")} left alone ·{" "}
            {results.length - count("updated") - count("added") - count("charity manages it (left alone)")} not matched
          </p>
          <div style={{ maxHeight: "360px", overflowY: "auto", fontSize: "15px", lineHeight: 1.7 }}>
            {results.map((r, i) => (
              <div key={`${r.placeId || r.name}-${i}`}>
                <strong>{r.name}</strong>{" "}
                <span style={{ color: r.status === "updated" || r.status === "added" ? "#4ADE80" : "#FFB86B" }}>
                  · {r.status}{r.charity ? ` · ${r.charity}` : ""}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
